<template>
  <div :title="''" class="bg-gray-100 min-h-screen">
    <!-- ALERT -->
    <alert-banner
      v-if="alert.state"
      :alert="alert"
      @close="alert.state = false"
      class="mb-6"
    />

    <!-- PAGE HEADER -->
    <div
      class="bg-white border border-gray-200 rounded-2xl shadow-sm px-8 py-7 mb-6"
    >
      <div
        class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5"
      >
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Security Settings</h1>

          <p class="text-gray-500 mt-2 max-w-2xl">
            Update your account password to keep your account secure and
            protected.
          </p>
        </div>

        <!-- SECURITY BADGE -->
        <div
          class="inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-green-50 border border-green-100"
        >
          <div
            class="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center"
          >
            <svg
              class="w-5 h-5 text-green-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 11c0 .552-.448 1-1 1s-1-.448-1-1 .448-1 1-1 1 .448 1 1zm0 0v2m6-6V7a6 6 0 10-12 0v2m12 0H6m12 0a2 2 0 012 2v7a2 2 0 01-2 2H6a2 2 0 01-2-2v-7a2 2 0 012-2"
              />
            </svg>
          </div>

          <div>
            <p class="text-sm font-semibold text-green-800">Account Security</p>

            <p class="text-xs text-green-600">Password encryption enabled</p>
          </div>
        </div>
      </div>
    </div>

    <!-- MAIN GRID -->
    <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <!-- PASSWORD FORM -->
      <div class="xl:col-span-2">
        <div
          class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
        >
          <!-- HEADER -->
          <div class="px-8 py-6 border-b border-gray-100">
            <h2 class="text-xl font-semibold text-gray-900">Change Password</h2>

            <p class="text-sm text-gray-500 mt-1">
              Use a strong password with a mix of letters, numbers and symbols.
            </p>
          </div>

          <!-- FORM -->
          <div class="p-8">
            <div class="space-y-6">
              <!-- CURRENT PASSWORD -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>

                <div class="relative">
                  <input
                    type="password"
                    v-model="userDetails.current_password"
                    placeholder="Enter current password"
                    class="w-full h-12 rounded-xl border border-gray-300 bg-white px-4 pr-12 text-sm focus:ring-2 focus:ring-app-500 focus:border-app-500 outline-none transition"
                  />

                  <div
                    class="absolute inset-y-0 right-4 flex items-center text-gray-400"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 10-8 0v4h8z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <!-- NEW PASSWORD -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>

                <div class="relative">
                  <input
                    type="password"
                    v-model="userDetails.new_password"
                    placeholder="Enter new password"
                    class="w-full h-12 rounded-xl border border-gray-300 bg-white px-4 pr-12 text-sm focus:ring-2 focus:ring-app-500 focus:border-app-500 outline-none transition"
                  />

                  <div
                    class="absolute inset-y-0 right-4 flex items-center text-gray-400"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                </div>

                <!-- PASSWORD STRENGTH -->
                <div class="mt-3">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-xs text-gray-500">
                      Password strength
                    </span>

                    <span
                      class="text-xs font-medium"
                      :class="passwordStrength.color"
                    >
                      {{ passwordStrength.label }}
                    </span>
                  </div>

                  <div
                    class="w-full h-2 rounded-full bg-gray-100 overflow-hidden"
                  >
                    <div
                      class="h-full rounded-full transition-all duration-300"
                      :class="passwordStrength.bg"
                      :style="{ width: passwordStrength.width }"
                    ></div>
                  </div>
                </div>
              </div>

              <!-- CONFIRM PASSWORD -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>

                <div class="relative">
                  <input
                    type="password"
                    v-model="userDetails.confirm_password"
                    @keypress.enter="resetPassword"
                    placeholder="Confirm new password"
                    class="w-full h-12 rounded-xl border border-gray-300 bg-white px-4 pr-12 text-sm focus:ring-2 focus:ring-app-500 focus:border-app-500 outline-none transition"
                  />

                  <div class="absolute inset-y-0 right-4 flex items-center">
                    <svg
                      v-if="
                        userDetails.confirm_password &&
                        userDetails.new_password ===
                          userDetails.confirm_password
                      "
                      class="w-5 h-5 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>

                <p
                  v-if="
                    userDetails.confirm_password &&
                    userDetails.new_password !== userDetails.confirm_password
                  "
                  class="mt-2 text-sm text-red-500"
                >
                  Passwords do not match
                </p>
              </div>

              <!-- ACTIONS -->
              <div
                class="pt-6 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
              >
                <div class="text-sm text-gray-500">Last updated recently</div>

                <div class="flex items-center gap-3">
                  <button
                    @click="resetFields"
                    class="h-11 px-5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 transition font-medium"
                  >
                    Cancel
                  </button>

                  <button
                    @click="resetPassword"
                    :disabled="
                      updatingPassword ||
                      userDetails.new_password !== userDetails.confirm_password
                    "
                    class="h-11 px-6 rounded-xl bg-app-600 hover:bg-app-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition flex items-center justify-center gap-2"
                  >
                    <svg
                      v-if="updatingPassword"
                      class="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-20"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />

                      <path
                        class="opacity-90"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      />
                    </svg>

                    <span>
                      {{
                        updatingPassword
                          ? "Updating Password..."
                          : "Update Password"
                      }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SECURITY PANEL -->
      <div class="space-y-6">
        <!-- SECURITY TIPS -->
        <div class="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
          <h3 class="text-base font-semibold text-gray-900 mb-5">
            Password Guidelines
          </h3>

          <div class="space-y-4">
            <div class="flex items-start gap-3">
              <div
                class="w-8 h-8 rounded-lg bg-app-100 flex items-center justify-center flex-shrink-0"
              >
                <svg
                  class="w-4 h-4 text-app-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <div>
                <p class="text-sm font-medium text-gray-800">
                  Use at least 8 characters
                </p>

                <p class="text-xs text-gray-500 mt-1">
                  Longer passwords are more secure.
                </p>
              </div>
            </div>

            <div class="flex items-start gap-3">
              <div
                class="w-8 h-8 rounded-lg bg-app-100 flex items-center justify-center flex-shrink-0"
              >
                <svg
                  class="w-4 h-4 text-app-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <div>
                <p class="text-sm font-medium text-gray-800">
                  Include uppercase & numbers
                </p>

                <p class="text-xs text-gray-500 mt-1">
                  Improve resistance against attacks.
                </p>
              </div>
            </div>

            <div class="flex items-start gap-3">
              <div
                class="w-8 h-8 rounded-lg bg-app-100 flex items-center justify-center flex-shrink-0"
              >
                <svg
                  class="w-4 h-4 text-app-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <div>
                <p class="text-sm font-medium text-gray-800">
                  Avoid reused passwords
                </p>

                <p class="text-xs text-gray-500 mt-1">
                  Unique passwords improve account safety.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- SECURITY STATUS -->
        <div
          class="bg-gradient-to-br from-app-600 to-app-700 rounded-2xl shadow-sm p-6 text-white"
        >
          <div
            class="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mb-5"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 11c0 .552-.448 1-1 1s-1-.448-1-1 .448-1 1-1 1 .448 1 1zm0 0v2m6-6V7a6 6 0 10-12 0v2m12 0H6m12 0a2 2 0 012 2v7a2 2 0 01-2 2H6a2 2 0 01-2-2v-7a2 2 0 012-2"
              />
            </svg>
          </div>

          <h3 class="text-lg font-semibold">Keep Your Account Secure</h3>

          <p class="text-app-100 text-sm mt-2 leading-relaxed">
            Regular password updates help protect sensitive tax audit and
            administrative operations.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AlertBanner from "@/components/AlertBanner.vue";
import { globalMixin } from "@/mixins/global.js";
import { username } from "@/helpers/login_details.js";

export default {
  components: {
    AlertBanner,
  },

  mixins: [globalMixin],

  data: () => ({
    updatingPassword: false,

    userDetails: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  }),

  computed: {
    passwordStrength() {
      const password = this.userDetails.new_password;

      if (!password) {
        return {
          label: "Weak",
          width: "10%",
          color: "text-gray-400",
          bg: "bg-gray-300",
        };
      }

      if (password.length < 6) {
        return {
          label: "Weak",
          width: "30%",
          color: "text-red-500",
          bg: "bg-red-500",
        };
      }

      if (password.length < 10) {
        return {
          label: "Medium",
          width: "65%",
          color: "text-amber-500",
          bg: "bg-amber-500",
        };
      }

      return {
        label: "Strong",
        width: "100%",
        color: "text-green-500",
        bg: "bg-green-500",
      };
    },
  },

  methods: {
    resetFields() {
      this.userDetails = {
        current_password: "",
        new_password: "",
        confirm_password: "",
      };
    },

    async resetPassword() {
      try {
        if (
          !this.userDetails.current_password ||
          !this.userDetails.new_password
        ) {
          this.showError("Please enter your current and new password.");

          return;
        }

        if (
          this.userDetails.new_password !== this.userDetails.confirm_password
        ) {
          this.showError("Passwords do not match");

          return;
        }

        this.updatingPassword = true;

        let result = await this.$store.dispatch("updatePassword", {
          currentPassword: this.userDetails.current_password,
          newPassword: this.userDetails.new_password,
          user: username,
        });

        if (result === "success") {
          this.showSuccess("Password updated successfully");

          this.resetFields();
        }
      } catch (error) {
        console.log(error);

        if (error.toLowerCase().includes("error")) {
          this.showError(error.substr(6, error.length));
        }
      } finally {
        this.updatingPassword = false;
      }
    },
  },
};
</script>
